import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { CalendarCheck2, ChevronRight, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const dayLabel = (iso) => {
  const d = new Date(`${iso}T12:00:00`);
  return { wd: d.toLocaleDateString("it-IT", { weekday: "short" }), n: d.getDate(), m: d.toLocaleDateString("it-IT", { month: "short" }) };
};
export const longDate = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" });

export const useSlots = () => {
  const [days, setDays] = useState([]);
  const [more, setMore] = useState(false);
  const load = useCallback(async (start = "", append = false) => {
    try {
      const { data } = await axios.get(`${API}/slots`, { params: { start, days: 14 } });
      const list = Array.isArray(data?.days) ? data.days : [];
      setDays((d) => (append ? [...d, ...list] : list));
      setMore(!!data?.has_more);
    } catch { setDays([]); }
  }, []);
  useEffect(() => { load(); }, [load]);
  return { days, more, load };
};

const Selected = ({ value, onClear }) => (
  <div className="mt-3 flex items-center justify-between gap-3 rounded-2xl bg-[#1B3B28] text-[#FCFBF7] px-5 py-4" data-testid="slot-selected">
    <span className="flex items-center gap-3"><CalendarCheck2 size={18} className="text-[#86C89B]" />
      <span><span className="block text-sm capitalize">{longDate(value.date)} alle {value.time}</span>
        <span className="block text-xs text-[#FCFBF7]/70">Lo staff ti confermerà l'orario via email</span></span></span>
    <button type="button" onClick={onClear} data-testid="slot-clear-btn" aria-label="Cambia orario" className="grid place-items-center w-9 h-9 rounded-full bg-[#FCFBF7]/10 hover:bg-[#FCFBF7]/20"><X size={15} /></button>
  </div>
);

export const SlotPicker = ({ value, onChange, slots }) => {
  const { days, more, load } = slots;
  const [day, setDay] = useState(null);
  const current = days.find((d) => d.date === day) || days.find((d) => d.slots.length);
  if (value) return <div className="sm:col-span-2"><span className="eyebrow text-[#526356]">Giorno e ora</span><Selected value={value} onClear={() => onChange(null)} /></div>;
  if (!days.length) return null;
  return (
    <div className="sm:col-span-2" data-testid="slot-picker">
      <span className="eyebrow text-[#526356]">Scegli giorno e ora <span className="normal-case tracking-normal">(facoltativo · visita di 45 minuti)</span></span>
      <div data-lenis-prevent className="mt-3 flex gap-2 overflow-x-auto pb-2 -mx-1 px-1">
        {days.map((d) => {
          const { wd, n, m } = dayLabel(d.date);
          const on = current?.date === d.date;
          return (
            <button type="button" key={d.date} disabled={!d.slots.length} onClick={() => setDay(d.date)} data-testid={`slot-day-${d.date}`}
              className={`shrink-0 w-[4.5rem] rounded-2xl border py-3 text-center transition-colors disabled:opacity-35 disabled:cursor-not-allowed ${on ? "bg-[#1B3B28] border-[#1B3B28] text-[#FCFBF7]" : "border-[#1B3B28]/15 text-[#1B3B28] hover:border-[#3E6B48]"}`}>
              <span className="block text-xs capitalize">{wd}</span><span className="block f-serif text-2xl leading-tight">{n}</span><span className="block text-xs">{m}</span>
            </button>
          );
        })}
        {more && (
          <button type="button" onClick={() => load(days[days.length - 1]?.date ? new Date(new Date(`${days[days.length - 1].date}T12:00:00`).getTime() + 864e5).toISOString().slice(0, 10) : "", true)}
            data-testid="slot-more-btn" className="shrink-0 w-[4.5rem] rounded-2xl border border-dashed border-[#1B3B28]/25 text-[#3E6B48] grid place-items-center text-xs">
            Altri giorni <ChevronRight size={14} />
          </button>
        )}
      </div>
      {current && (
        <div className="mt-3 grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="slot-times">
          {current.slots.map((t) => (
            <button type="button" key={t} onClick={() => onChange({ date: current.date, time: t })} data-testid={`slot-time-${t.replace(":", "")}`}
              className="rounded-xl border border-[#1B3B28]/15 py-2.5 text-sm text-[#1B3B28] hover:bg-[#E6ECE4] hover:border-[#3E6B48] transition-colors">{t}</button>
          ))}
        </div>
      )}
      <p className="text-xs text-[#526356] mt-3">Preferisci parlarne al telefono? Lascia vuoto: ti richiamiamo noi.</p>
    </div>
  );
};
